import React, { useContext, useState } from "react";
import { Lock, Check, Layout } from "lucide-react";
import { 
  Description, 
  Header, 
  TemplateCard, 
  TemplateContent, 
  TemplateName, 
  Container, 
  Price, 
  Grid, 
  Badge, 
  PreviewImage, 
  TemplateDesc, 
  PriceContainer, 
  ActionButton,
  ButtonSpinner,
  PromoBanner,
  PromoText,
  PromoCodeBadge
} from './choose-template.styles'
import { useChangeTemplate, useGetFullPortfolio } from "../../services/api/users-api";
import { ToastContext } from "../../services/contexts/toast-context";
import { useGetTemplates } from "../../services/api/subscription-api";
import { useNavigate } from "react-router-dom";
import Loading from "../../components/loading";

const ChooseTemplate = () => {
  const { mutate: updateTemplate, isLoading: isUpdatingTemplate } = useChangeTemplate()
  const { data: response = {}, isLoading: isLoadingTemplates, error: fetchTemplatesError } = useGetTemplates()
  const { data: profileData = {} } = useGetFullPortfolio()
  const templates = Array.isArray(response?.data?.results) ? response.data.results : (Array.isArray(response?.data) ? response.data : []);
  const currentSelectedTemplateId = Number(
    profileData?.selected_template ??
    profileData?.selected_template_id ??
    profileData?.profile?.selected_template ??
    profileData?.profile?.selected_template_id ??
    0
  ) || null;
  const [pendingTemplateId, setPendingTemplateId] = useState(null);
  const toastRef = useContext(ToastContext);
  const navigate  = useNavigate()

  const handleTemplateClick = async (template) => {
    const isPaid = parseFloat(template.price) > 0 && !template.is_purchased;

    if (!isPaid && Number(template.id) === currentSelectedTemplateId) {
      return;
    }
    
    if (isPaid) {
      navigate(`/purchase?id=${template.id}&type=template&name=${encodeURIComponent(template.name)}&price=${template.price}`);
      return;
    }

    updateTemplate({template_id: template.id }, {
      onSuccess: () => {
        setPendingTemplateId(null);
        toastRef.current.show({ severity: 'success', summary: 'Success', detail: 'Updated Template successfully', life: 3000 });
      },
      onError: (error) => {
        setPendingTemplateId(null);
        toastRef.current.show({ severity: 'error', summary: 'Error', detail: error.message || 'Failed to update template', life: 3000 });
      }
    });

    setPendingTemplateId(template.id);
  };

  const handleCopyPromoCode = () => {
    navigator.clipboard.writeText('LAUNCH2026').then(() => {
      toastRef.current.show({ severity: 'success', summary: 'Copied!', detail: 'Promo code copied to clipboard', life: 3000 });
    }).catch(() => {
      toastRef.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to copy promo code', life: 3000 });
    });
  };

  return (
    <Container>
      <PromoBanner>
        <PromoText>
          🎉 Surprise! To celebrate our launch, all premium templates are <strong>100% FREE</strong> for a limited time — use code:
        </PromoText>
        <PromoCodeBadge onClick={handleCopyPromoCode} type="button">
          LAUNCH2026 📋
        </PromoCodeBadge>
      </PromoBanner>
      <Header>Choose your look</Header>
      <Description>
        Switch between professional templates instantly. Premium templates include advanced layouts and sections.
      </Description>
      
      {isLoadingTemplates ? (
        <Loading />
      ) : fetchTemplatesError ? (
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <p style={{ color: "red" }}>Failed to load templates. Please try again later.</p>
        </div>
      ) : (
        <Grid>
          {templates.map((template) => {
            const isPaid = parseFloat(template.price) > 0 && !template.is_purchased;
            const isFree = parseFloat(template.price) === 0;
            const isSelected = currentSelectedTemplateId === Number(template.id);
            const isTemplateLoading = isUpdatingTemplate && pendingTemplateId === template.id;
            const templateImage = template?.name?.toLowerCase() === "sabri" ? "/sabri-template.png" : null;
            
            return (
              <TemplateCard
                key={template.id}
                isSelected={isSelected}
                onClick={() => handleTemplateClick(template)}
              >
                {template.is_purchased && <Badge purchased>Purchased</Badge>}
                {!template.is_purchased && isPaid && <Badge>Premium</Badge>}
                
                <PreviewImage>
                  {templateImage ? (
                    <img
                      src={templateImage}
                      alt={`${template.name} template preview`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <Layout size={48} strokeWidth={1} />
                  )}
                </PreviewImage>

                <TemplateContent>
                  <TemplateName>{template.name}</TemplateName>
                  <TemplateDesc>{template.description}</TemplateDesc>
                  
                  <PriceContainer>
                    <Price>
                      {isFree ? 'Free' : `${template.price} TND`}
                    </Price>
                    <ActionButton primary={isPaid} disabled={(isSelected && !isPaid) || isTemplateLoading}>
                      {isTemplateLoading ? (
                        <>Loading <ButtonSpinner /></>
                      ) : isPaid ? (
                        <>Unlock <Lock size={14} style={{ marginLeft: '4px' }} /></>
                      ) : isSelected ? (
                        <>Selected <Check size={14} style={{ marginLeft: '4px' }} /></>
                      ) : (
                        <>Use Template <Check size={14} style={{ marginLeft: '4px' }} /></>
                      )}
                    </ActionButton>
                  </PriceContainer>
                </TemplateContent>
              </TemplateCard>
            );
          })}
        </Grid>
      )}
    </Container>
  );
};

export default ChooseTemplate;

